import React, { useState, useEffect } from "react";
import {
  Search,
  Edit,
  Save,
  X,
  TrendingUp,
  Target,
  BarChart3,
  Calendar,
  User,
  AlertCircle,
  CheckCircle2,
  Filter,
} from "lucide-react";
import { authenticatedFetch } from '../services/auth';

const IndicatorValueManagement = ({ onClose }) => {
  const [indicatorValues, setIndicatorValues] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    actual: "",
    target: "",
    result_analysis: "",
    proposed_actions: "",
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    const initializeData = async () => {
      await fetchPeriods();
    };
    initializeData();
  }, []);

  const fetchPeriods = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/periods-management`);
      if (response.ok) {
        const data = await response.json();
        const activePeriods =
          data && Array.isArray(data) ? data.filter((p) => p.is_active) : [];
        setPeriods(activePeriods);

        // Definir automaticamente o último período ativo
        if (activePeriods.length > 0) {
          const latestPeriod = activePeriods.sort(
            (a, b) => new Date(b.start_date) - new Date(a.start_date),
          )[0];
          setSelectedPeriod(latestPeriod.period_id);
          await fetchIndicatorValues(latestPeriod.period_id);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar períodos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndicatorValues = async (periodId = "") => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/api/indicator-values`;
      if (periodId) {
        url += `?period_id=${periodId}`;
      }

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setIndicatorValues(Array.isArray(data) ? data : []);
      } else {
        // Não manter dados de um período anterior na tela em caso de erro
        console.error("Erro ao buscar valores:", response.status);
        setIndicatorValues([]);
      }
    } catch (error) {
      console.error("Erro ao carregar valores dos indicadores:", error);
      setIndicatorValues([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (periodId) => {
    setSelectedPeriod(periodId);
    fetchIndicatorValues(periodId);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      actual: item.actual || "",
      target: item.target || "",
      result_analysis: item.result_analysis || "",
      proposed_actions: item.proposed_actions || "",
    });
  };

  const handleSave = async () => {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/indicator-values/${editingItem.id}`,
        {
          method: "PUT",
          body: JSON.stringify(formData),
        },
      );

      if (response.ok) {
        await fetchIndicatorValues(selectedPeriod);
        setEditingItem(null);
        setFormData({
          actual: "",
          target: "",
          result_analysis: "",
          proposed_actions: "",
        });
        alert("Valores atualizados com sucesso!");
      } else {
        throw new Error("Erro ao salvar");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      alert("Erro ao salvar valores");
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setFormData({
      actual: "",
      target: "",
      result_analysis: "",
      proposed_actions: "",
    });
  };

  const filteredValues = indicatorValues.filter(
    (item) =>
      item.indicator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.perspective_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.objective_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.responsible_user?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatValue = (value, unit) => {
    if (value === null || value === undefined) return "-";
    if (unit === "R$") {
      return `R$ ${value.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    }
    return `${value} ${unit || ""}`.trim();
  };

  const getPerformanceColor = (percentual) => {
    if (!percentual) return "text-gray-500";
    const percentage = (percentual);
    if (percentage >= 100) return "text-green-600";
    if (percentage >= 80) return "text-yellow-600";
    return "text-red-600";
  };

  const getPerformanceIcon = (percentual) => {
    if (!percentual) return <AlertCircle className="w-4 h-4" />;
    const percentage = (percentual);
    if (percentage >= 100) return <CheckCircle2 className="w-4 h-4" />;
    if (percentage >= 80) return <TrendingUp className="w-4 h-4" />;
    return <AlertCircle className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header do Mapa Estratégico */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">

            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Lançamento de Valores
                </h1>
                <p className="text-gray-600 text-sm">
                  Gerenciar valores mensais dos indicadores BSC
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="w-full mx-auto p-6">
        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar indicadores, perspectivas, objetivos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={selectedPeriod}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Selecione um Período</option>
                {periods.map((period) => (
                  <option key={period.period_id} value={period.period_id}>
                    {period.period_code} -{" "}
                    {period.description || "Sem descrição"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabela de Valores */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-600">
                Carregando valores dos indicadores...
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Período
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Perspectiva
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Objetivo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Indicador
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsável
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Meta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Realizado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Peso
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredValues.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center text-sm">
                          <Calendar className="w-4 h-4 text-blue-500 mr-1" />
                          <span className="font-medium text-blue-900">
                            {item.period_code || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {item.perspective_name || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {item.objective_name || "-"}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <BarChart3 className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.indicator_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.measure_unit}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <User className="w-4 h-4 text-gray-400 mr-1" />
                          {item.responsible_user || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {editingItem?.id === item.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={formData.target}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                target: e.target.value,
                              })
                            }
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            placeholder="0.00"
                          />
                        ) : (
                          <div className="flex items-center text-sm">
                            <Target className="w-4 h-4 text-blue-500 mr-1" />
                            <span className="font-medium text-blue-900">
                              {formatValue(item.target, item.measure_unit)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {editingItem?.id === item.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={formData.actual}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                actual: e.target.value,
                              })
                            }
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            placeholder="0.00"
                          />
                        ) : (
                          <span className="text-sm font-medium text-gray-900">
                            {formatValue(item.actual, item.measure_unit)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div
                          className={`flex items-center ${getPerformanceColor(
                            item.percentual
                          )}`}
                        >
                          {getPerformanceIcon(item.percentual)}
                          <span className="ml-1 text-sm font-medium">
                            {item.actual && item.target
                              ? `${(item.percentual).toFixed(1)}%`
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700"
                          title="Peso (snapshot do período) usado no cálculo da performance do objetivo"
                        >
                          {item.weight != null ? parseFloat(item.weight) : 1}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {editingItem?.id === item.id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={handleSave}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredValues.length === 0 && !loading && (
                <div className="p-12 text-center">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  {periods.length === 0 ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Nenhum dado disponível
                      </h3>
                      <p className="text-gray-600">
                        Nenhum período foi encontrado para exibir.
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-600">
                      {selectedPeriod
                        ? "Nenhum valor de indicador encontrado para o período selecionado"
                        : "Selecione um período para visualizar os valores dos indicadores"}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal de Edição Expandida */}
        {editingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Editar: {editingItem.indicator_name}
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Perspectiva
                    </label>
                    <input
                      type="text"
                      value={editingItem.perspective_name || ""}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Objetivo
                    </label>
                    <input
                      type="text"
                      value={editingItem.objective_name || ""}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Responsável
                    </label>
                    <input
                      type="text"
                      value={editingItem.responsible_user || ""}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={formData.target}
                        onChange={(e) =>
                          setFormData({ ...formData, target: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-2 text-sm text-gray-500">
                        {editingItem.measure_unit}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Realizado *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={formData.actual}
                        onChange={(e) =>
                          setFormData({ ...formData, actual: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-2 text-sm text-gray-500">
                        {editingItem.measure_unit}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unidade de Medida
                    </label>
                    <input
                      type="text"
                      value={editingItem.measure_unit || ""}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Análise do Resultado
                  </label>
                  <textarea
                    value={formData.result_analysis}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        result_analysis: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows="4"
                    placeholder="Descreva a análise do resultado alcançado..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ações Propostas
                  </label>
                  <textarea
                    value={formData.proposed_actions}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        proposed_actions: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows="4"
                    placeholder="Descreva as ações propostas para melhorar o resultado..."
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndicatorValueManagement;
